import { defineStaffAction, defineSystemAction, defineUserAction } from "./types";

/**
 * Every action in Walliyo, in one list (WALLIYO-0069).
 *
 * To add an action:
 *   1. add it here with the define function for its actor. Its `actionId` is
 *      <VERB_OBJECT>_<USER|STAFF|SYSTEM>_ACTION and permanent;
 *   2. name the feature enablement that allows it, or `null` if it is always
 *      available. If no feature fits, add one to SETTINGS first;
 *   3. if it moves a job between statuses, add it to JOB_TRANSITIONS.
 * `definitions.t.ts` checks that every feature unlocks at least one action.
 */

export const ACTIONS = [
  // ── Customers: Account ───────────────────────────────────────────────────
  defineUserAction({
    actionId: "SIGN_UP_USER_ACTION",
    name: "Sign up",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "SIGN_IN_USER_ACTION",
    name: "Sign in",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "SIGN_OUT_USER_ACTION",
    name: "Sign out",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "UPDATE_PERSONAL_INFO_USER_ACTION",
    name: "Update personal details",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: null,
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "CHANGE_PHONE_USER_ACTION",
    name: "Change phone number",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: "CHANGE_PHONE_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  defineUserAction({
    actionId: "CHANGE_EMAIL_USER_ACTION",
    name: "Change email address",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: "CHANGE_EMAIL_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  defineUserAction({
    actionId: "CHANGE_PASSWORD_USER_ACTION",
    name: "Change password",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "SWITCH_MODE_USER_ACTION",
    name: "Switch between client and pro",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: "SWITCH_MODE_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "CREATE_CLIENT_PROFILE_USER_ACTION",
    name: "Create a client profile",
    profile: "pro",
    area: "account",
    effect: "write",
    target: "client_profile",
    requiredFeature: "CREATE_CLIENT_PROFILE_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "UPDATE_SETTING_USER_ACTION",
    name: "Change a setting",
    profile: "either",
    area: "settings",
    effect: "write",
    target: "setting_value",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "VIEW_SECURITY_ACTIVITY_USER_ACTION",
    name: "View security activity",
    profile: "either",
    area: "account",
    effect: "read",
    target: "audit_log",
    requiredFeature: "SECURITY_ACTIVITY_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  defineUserAction({
    actionId: "REFER_FRIEND_USER_ACTION",
    name: "Refer a friend",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: "REFER_FRIENDS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  defineUserAction({
    actionId: "DELETE_ACCOUNT_USER_ACTION",
    name: "Delete the account",
    profile: "either",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  // ── Customers: Becoming and being a pro ──────────────────────────────────
  defineUserAction({
    actionId: "BECOME_PRO_USER_ACTION",
    name: "Become a pro",
    profile: "client",
    area: "onboarding",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "BECOME_PRO_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "SUBMIT_VERIFICATION_USER_ACTION",
    name: "Submit an ID for verification",
    profile: "pro",
    area: "onboarding",
    effect: "write",
    target: "identity_verification",
    requiredFeature: "SUBMIT_VERIFICATION_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "UPDATE_PRO_PROFILE_USER_ACTION",
    name: "Update the pro profile",
    profile: "pro",
    area: "onboarding",
    effect: "write",
    target: "pro_profile",
    requiredFeature: null,
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "UPDATE_RATES_USER_ACTION",
    name: "Update rates",
    profile: "pro",
    area: "onboarding",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "EDIT_RATES_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "ADD_PORTFOLIO_PHOTO_USER_ACTION",
    name: "Add a portfolio photo",
    profile: "pro",
    area: "onboarding",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "PORTFOLIO_PHOTOS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "REMOVE_PORTFOLIO_PHOTO_USER_ACTION",
    name: "Remove a portfolio photo",
    profile: "pro",
    area: "onboarding",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "PORTFOLIO_PHOTOS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  // ── Customers: Leads ─────────────────────────────────────────────────────
  defineUserAction({
    actionId: "UPDATE_LEAD_PREFERENCES_USER_ACTION",
    name: "Update lead preferences",
    profile: "pro",
    area: "leads",
    effect: "write",
    target: "setting_value",
    requiredFeature: "LEAD_PREFERENCES_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "PAUSE_LEADS_USER_ACTION",
    name: "Pause leads",
    profile: "pro",
    area: "leads",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "PAUSE_LEADS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "RESUME_LEADS_USER_ACTION",
    name: "Resume leads",
    profile: "pro",
    area: "leads",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "PAUSE_LEADS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "VIEW_OPEN_JOBS_USER_ACTION",
    name: "View open jobs",
    profile: "pro",
    area: "leads",
    effect: "read",
    target: "job",
    requiredFeature: "BROWSE_OPEN_JOBS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "VIEW_ACTIVITY_USER_ACTION",
    name: "View activity",
    profile: "pro",
    area: "leads",
    effect: "read",
    target: "pro_profile",
    requiredFeature: "ACTIVITY_PAGE_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  // ── Customers: Jobs: the client's side ───────────────────────────────────
  defineUserAction({
    actionId: "REQUEST_PRO_USER_ACTION",
    name: "Request a pro",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "REQUEST_PRO_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "POST_OPEN_JOB_USER_ACTION",
    name: "Post an open job",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "POST_OPEN_JOB_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "REBOOK_PRO_USER_ACTION",
    name: "Rebook a pro",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "REBOOK_PRO_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "UPDATE_JOB_USER_ACTION",
    name: "Update a job",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: null,
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "ADD_JOB_PHOTOS_USER_ACTION",
    name: "Add job photos",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "JOB_PHOTOS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "SCHEDULE_JOB_USER_ACTION",
    name: "Schedule a job",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "SCHEDULE_JOB_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "MARK_JOB_URGENT_USER_ACTION",
    name: "Mark a job urgent",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "URGENT_JOB_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "SELECT_QUOTE_USER_ACTION",
    name: "Select a quote",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "quote",
    requiredFeature: "SELECT_QUOTE_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "CANCEL_JOB_USER_ACTION",
    name: "Cancel a job",
    profile: "client",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: null,
    onBehalfAllowed: true,
  }),
  // ── Customers: Jobs: the pro's side ──────────────────────────────────────
  defineUserAction({
    actionId: "SUBMIT_QUOTE_USER_ACTION",
    name: "Send a quote",
    profile: "pro",
    area: "jobs",
    effect: "write",
    target: "quote",
    requiredFeature: "QUOTE_JOBS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "UPDATE_QUOTE_USER_ACTION",
    name: "Update a quote",
    profile: "pro",
    area: "jobs",
    effect: "write",
    target: "quote",
    requiredFeature: "QUOTE_JOBS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "WITHDRAW_QUOTE_USER_ACTION",
    name: "Withdraw a quote",
    profile: "pro",
    area: "jobs",
    effect: "write",
    target: "quote",
    requiredFeature: "QUOTE_JOBS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "ACCEPT_JOB_USER_ACTION",
    name: "Accept a job",
    profile: "pro",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "QUOTE_JOBS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "DECLINE_JOB_USER_ACTION",
    name: "Decline a job",
    profile: "pro",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "QUOTE_JOBS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "DECLARE_JOB_OUTCOME_USER_ACTION",
    name: "Declare how a job ended",
    profile: "either",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "DECLARE_JOB_OUTCOME_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  // ── Customers: Finding pros ──────────────────────────────────────────────
  defineUserAction({
    actionId: "SEARCH_PROS_USER_ACTION",
    name: "Search pros",
    profile: "client",
    area: "discovery",
    effect: "read",
    target: "pro_profile",
    requiredFeature: "SEARCH_PROS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "SEARCH_PROS_NEARBY_USER_ACTION",
    name: "Search pros nearby",
    profile: "client",
    area: "discovery",
    effect: "read",
    target: "pro_profile",
    requiredFeature: "NEARBY_PROS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "VIEW_PRO_PROFILE_USER_ACTION",
    name: "View a pro's profile",
    profile: "client",
    area: "discovery",
    effect: "read",
    target: "pro_profile",
    requiredFeature: null,
    onBehalfAllowed: true,
  }),
  defineUserAction({
    actionId: "SAVE_FAVORITE_PRO_USER_ACTION",
    name: "Save a favourite pro",
    profile: "client",
    area: "discovery",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "SAVE_FAVORITE_PROS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "REMOVE_FAVORITE_PRO_USER_ACTION",
    name: "Remove a favourite pro",
    profile: "client",
    area: "discovery",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "SAVE_FAVORITE_PROS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: true,
    status: "planned",
  }),
  defineUserAction({
    actionId: "SHARE_PRO_PROFILE_USER_ACTION",
    name: "Share a pro's profile",
    profile: "client",
    area: "discovery",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "SHARE_PRO_PROFILE_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  // ── Customers: Messaging ─────────────────────────────────────────────────
  defineUserAction({
    actionId: "OPEN_CONVERSATION_USER_ACTION",
    name: "Open a conversation",
    profile: "either",
    area: "messaging",
    effect: "write",
    target: "conversation",
    requiredFeature: "MESSAGING_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "SEND_MESSAGE_USER_ACTION",
    name: "Send a message",
    profile: "either",
    area: "messaging",
    effect: "write",
    target: "message",
    requiredFeature: "MESSAGING_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "SEND_PHOTO_USER_ACTION",
    name: "Send a photo",
    profile: "either",
    area: "messaging",
    effect: "write",
    target: "message",
    requiredFeature: "SEND_PHOTOS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "SHARE_LOCATION_USER_ACTION",
    name: "Share a location",
    profile: "either",
    area: "messaging",
    effect: "write",
    target: "message",
    requiredFeature: "SHARE_LOCATION_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "READ_MESSAGES_USER_ACTION",
    name: "Read messages",
    profile: "either",
    area: "messaging",
    effect: "read",
    target: "conversation",
    requiredFeature: "MESSAGING_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "SAVE_QUICK_REPLY_USER_ACTION",
    name: "Save a quick reply",
    profile: "pro",
    area: "messaging",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "QUICK_REPLIES_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  // ── Customers: Reviews ───────────────────────────────────────────────────
  defineUserAction({
    actionId: "LEAVE_REVIEW_USER_ACTION",
    name: "Leave a review",
    profile: "client",
    area: "reviews",
    effect: "write",
    target: "review",
    requiredFeature: "LEAVE_REVIEWS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  // ── Customers: Payments ──────────────────────────────────────────────────
  defineUserAction({
    actionId: "SUBSCRIBE_TO_PLAN_USER_ACTION",
    name: "Subscribe to a plan",
    profile: "pro",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: "PRO_SUBSCRIPTIONS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "CANCEL_SUBSCRIPTION_USER_ACTION",
    name: "Cancel the renewal",
    profile: "pro",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  // Undoes a cancel before the paid period ends: the same subscription.
  defineUserAction({
    actionId: "RESUME_SUBSCRIPTION_USER_ACTION",
    name: "Resume the renewal",
    profile: "pro",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  // The new plan takes over when the paid period ends (ADR-0018 §12).
  defineUserAction({
    actionId: "CHANGE_PLAN_USER_ACTION",
    name: "Change plan",
    profile: "pro",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: "PRO_SUBSCRIPTIONS_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "PAY_FEE_USER_ACTION",
    name: "Pay a fee",
    profile: "pro",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: null,
    onBehalfAllowed: false,
  }),
  // ── Customers: Support and safety ────────────────────────────────────────
  defineUserAction({
    actionId: "CONTACT_SUPPORT_USER_ACTION",
    name: "Contact support",
    profile: "either",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "CONTACT_US_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "REQUEST_CALLBACK_USER_ACTION",
    name: "Request a callback",
    profile: "client",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "REQUEST_CALLBACK_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  defineUserAction({
    actionId: "REPORT_USER_USER_ACTION",
    name: "Report someone",
    profile: "either",
    area: "safety",
    effect: "write",
    target: "safety_report",
    requiredFeature: "REPORT_USER_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),
  defineUserAction({
    actionId: "BLOCK_USER_USER_ACTION",
    name: "Block someone",
    profile: "either",
    area: "safety",
    effect: "write",
    target: "user",
    requiredFeature: "BLOCK_USER_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  defineUserAction({
    actionId: "UNBLOCK_USER_USER_ACTION",
    name: "Unblock someone",
    profile: "either",
    area: "safety",
    effect: "write",
    target: "user",
    requiredFeature: "BLOCK_USER_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
    status: "planned",
  }),
  defineUserAction({
    actionId: "APPEAL_DECISION_USER_ACTION",
    name: "Appeal a decision",
    profile: "either",
    area: "safety",
    effect: "write",
    target: "safety_report",
    requiredFeature: "APPEAL_DECISION_USER_FEATURE_ENABLEMENT",
    onBehalfAllowed: false,
  }),

  // ── Staff: their own account ────────────────────────────────────────────
  // ── Experiments and growth (WALLIYO-0132) ──────────────────────────────
  defineStaffAction({
    actionId: "CREATE_EXPERIMENT_STAFF_ACTION",
    name: "Start an experiment",
    area: "settings",
    effect: "write",
    target: "experiment",
    requiredFeature: "MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT",
  }),
  // Ramping is this: the same action at a bigger share, audited each step.
  defineStaffAction({
    actionId: "MOVE_EXPERIMENT_STAFF_ACTION",
    name: "Ramp or stop an experiment",
    area: "settings",
    effect: "write",
    target: "experiment",
    requiredFeature: "MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "ADD_LEAD_STAFF_ACTION",
    name: "Add a lead",
    area: "leads",
    effect: "write",
    target: "lead",
    requiredFeature: "MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "MOVE_LEAD_STAFF_ACTION",
    name: "Move a lead along",
    area: "leads",
    effect: "write",
    target: "lead",
    requiredFeature: "MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "CREATE_CAMPAIGN_STAFF_ACTION",
    name: "Draft a campaign",
    area: "leads",
    effect: "write",
    target: "campaign",
    requiredFeature: "MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT",
  }),
  // Sending is its own action: drafting one costs nothing and sending an SMS
  // campaign spends money.
  defineStaffAction({
    actionId: "SEND_CAMPAIGN_STAFF_ACTION",
    name: "Send a campaign",
    area: "leads",
    effect: "write",
    target: "campaign",
    requiredFeature: "MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT",
  }),

  // ── Incidents (WALLIYO-0132, #126) ─────────────────────────────────────
  // Opening one puts a banner in front of every customer, so it is a write
  // and it is audited like every other.
  defineStaffAction({
    actionId: "OPEN_INCIDENT_STAFF_ACTION",
    name: "Open an incident",
    area: "settings",
    effect: "write",
    target: "incident",
    requiredFeature: "MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "UPDATE_INCIDENT_STAFF_ACTION",
    name: "Update an incident",
    area: "settings",
    effect: "write",
    target: "incident",
    requiredFeature: "MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT",
  }),

  // ── Marketplace operations (WALLIYO-0132) ──────────────────────────────
  // Reads, all of them: this workspace watches the market, it does not move
  // it. A job is moved by the people on it, through the app.
  defineStaffAction({
    actionId: "VIEW_MARKETPLACE_STAFF_ACTION",
    name: "See how the marketplace is doing",
    area: "jobs",
    effect: "read",
    target: "job",
    requiredFeature: "VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "VIEW_MARKETPLACE_JOB_STAFF_ACTION",
    name: "Look at a job",
    area: "jobs",
    effect: "read",
    target: "job",
    requiredFeature: "VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "SIGN_IN_STAFF_ACTION",
    name: "Sign in to the console",
    area: "staff",
    effect: "write",
    target: "staff_session",
    requiredFeature: null,
  }),
  defineStaffAction({
    actionId: "SIGN_OUT_STAFF_ACTION",
    name: "Sign out of the console",
    area: "staff",
    effect: "write",
    target: "staff_session",
    requiredFeature: null,
  }),
  defineStaffAction({
    // An invited staff member sets their first password with the code sent
    // by SMS: a code needed before they can use the console (WALLIYO-0098).
    // Before a session exists, like signing in.
    actionId: "SET_PASSWORD_STAFF_ACTION",
    name: "Set a console password",
    area: "staff",
    effect: "write",
    target: "staff_member",
    requiredFeature: null,
  }),
  defineStaffAction({
    actionId: "UPDATE_STAFF_SETTING_STAFF_ACTION",
    name: "Change a console setting",
    area: "settings",
    effect: "write",
    target: "setting_value",
    requiredFeature: null,
    status: "active",
  }),

  // ── Staff: accounts and support ─────────────────────────────────────────
  // Searching for and opening a customer are recorded reads (ADR-0020 §7.1,
  // amending ADR-0015 §2.1): a staffer who browses many customers leaves a
  // trail, not only the one who reveals a phone number.
  defineStaffAction({
    actionId: "SEARCH_ACCOUNTS_STAFF_ACTION",
    name: "Search accounts",
    area: "account",
    effect: "read",
    target: "user",
    requiredFeature: "SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
    status: "active",
  }),
  defineStaffAction({
    actionId: "VIEW_ACCOUNT_STAFF_ACTION",
    name: "View an account",
    area: "account",
    effect: "read",
    target: "user",
    requiredFeature: "VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
    status: "active",
  }),
  defineStaffAction({
    actionId: "REVEAL_CONTACT_DETAILS_STAFF_ACTION",
    name: "Reveal a phone number",
    area: "account",
    effect: "read",
    target: "user",
    requiredFeature: "REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
    status: "active",
  }),
  defineStaffAction({
    actionId: "EDIT_CUSTOMER_SETTING_STAFF_ACTION",
    name: "Edit a customer's setting",
    area: "settings",
    effect: "write",
    target: "setting_value",
    requiredFeature: "EDIT_CUSTOMER_SETTINGS_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineStaffAction({
    actionId: "CORRECT_ACCOUNT_DETAILS_STAFF_ACTION",
    name: "Correct account details",
    area: "account",
    effect: "write",
    target: "user",
    requiredFeature: "CORRECT_ACCOUNT_DETAILS_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineStaffAction({
    actionId: "CLAIM_CASE_STAFF_ACTION",
    name: "Claim a case",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "LOG_CALL_STAFF_ACTION",
    name: "Log a call",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "REPLY_TO_CASE_STAFF_ACTION",
    name: "Reply to a customer",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "ADD_CASE_NOTE_STAFF_ACTION",
    name: "Add an internal note",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "CLOSE_CASE_STAFF_ACTION",
    name: "Close a case",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "REASSIGN_CASE_STAFF_ACTION",
    name: "Reassign a case",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "REASSIGN_CASES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    // Who an unknown caller turned out to be: the case's customer.
    actionId: "LINK_CASE_CUSTOMER_STAFF_ACTION",
    name: "Link a customer to a case",
    area: "support",
    effect: "write",
    target: "support_case",
    requiredFeature: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "START_ON_BEHALF_SESSION_STAFF_ACTION",
    name: "Start acting on behalf of a customer",
    area: "support",
    effect: "write",
    target: "on_behalf_session",
    requiredFeature: "ACT_ON_BEHALF_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineStaffAction({
    actionId: "END_ON_BEHALF_SESSION_STAFF_ACTION",
    name: "Stop acting on behalf of a customer",
    area: "support",
    effect: "write",
    target: "on_behalf_session",
    requiredFeature: "ACT_ON_BEHALF_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),

  // ── Staff: onboarding ───────────────────────────────────────────────────
  defineStaffAction({
    actionId: "VIEW_VERIFICATION_STAFF_ACTION",
    name: "View a verification",
    area: "onboarding",
    effect: "read",
    target: "identity_verification",
    requiredFeature: "REVIEW_VERIFICATIONS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "APPROVE_VERIFICATION_STAFF_ACTION",
    name: "Approve a verification",
    area: "onboarding",
    effect: "write",
    target: "identity_verification",
    requiredFeature: "REVIEW_VERIFICATIONS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "REJECT_VERIFICATION_STAFF_ACTION",
    name: "Reject a verification",
    area: "onboarding",
    effect: "write",
    target: "identity_verification",
    requiredFeature: "REVIEW_VERIFICATIONS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "VIEW_ID_IMAGES_STAFF_ACTION",
    name: "View ID images",
    area: "onboarding",
    effect: "read",
    target: "identity_verification",
    requiredFeature: "VIEW_ID_IMAGES_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
  }),
  defineStaffAction({
    actionId: "APPROVE_PRO_STAFF_ACTION",
    name: "Approve a pro",
    area: "onboarding",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "APPROVE_PROS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "REJECT_PRO_STAFF_ACTION",
    name: "Reject a pro",
    area: "onboarding",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "APPROVE_PROS_STAFF_FEATURE_ENABLEMENT",
  }),

  // ── Staff: payments ─────────────────────────────────────────────────────
  defineStaffAction({
    actionId: "VIEW_PAYMENTS_STAFF_ACTION",
    name: "View payments",
    area: "payments",
    effect: "read",
    target: "platform_fee_payment",
    requiredFeature: "VIEW_PAYMENTS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "ISSUE_REFUND_STAFF_ACTION",
    name: "Issue a refund",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: "ISSUE_REFUNDS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "ISSUE_CREDIT_STAFF_ACTION",
    name: "Issue a credit",
    area: "payments",
    effect: "write",
    target: "pro_profile",
    requiredFeature: "ISSUE_CREDITS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "WAIVE_FEE_STAFF_ACTION",
    name: "Waive a fee",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: "WAIVE_FEES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "MARK_PAYMENT_PAID_STAFF_ACTION",
    name: "Mark a fee paid",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: "MARK_PAYMENTS_PAID_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "APPROVE_PAYMENT_REQUEST_STAFF_ACTION",
    name: "Approve a request",
    area: "payments",
    effect: "write",
    target: "approval_request",
    requiredFeature: "APPROVE_PAYMENT_REQUESTS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "REJECT_PAYMENT_REQUEST_STAFF_ACTION",
    name: "Reject a request",
    area: "payments",
    effect: "write",
    target: "approval_request",
    requiredFeature: "APPROVE_PAYMENT_REQUESTS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    // How a refund sent to the provider, or transferred by hand, ended:
    // Finance records it once, with the provider's reference.
    actionId: "SETTLE_REFUND_STAFF_ACTION",
    name: "Settle a refund",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: "ISSUE_REFUNDS_STAFF_FEATURE_ENABLEMENT",
  }),
  // The commercial console (ADR-0018 §4a).
  defineStaffAction({
    actionId: "SET_PLAN_PRICE_STAFF_ACTION",
    name: "Set a plan's price",
    area: "payments",
    effect: "write",
    target: "plan_price",
    requiredFeature: "MANAGE_PLAN_PRICES_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "CREATE_PROMO_STAFF_ACTION",
    name: "Create a promotion",
    area: "payments",
    effect: "write",
    target: "plan_promo",
    requiredFeature: "RUN_PROMOS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "END_PROMO_STAFF_ACTION",
    name: "End a promotion",
    area: "payments",
    effect: "write",
    target: "plan_promo",
    requiredFeature: "RUN_PROMOS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "CREATE_DISCOUNT_STAFF_ACTION",
    name: "Create a discount",
    area: "payments",
    effect: "write",
    target: "pro_discount",
    requiredFeature: "OFFER_DISCOUNTS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "END_DISCOUNT_STAFF_ACTION",
    name: "End a discount",
    area: "payments",
    effect: "write",
    target: "pro_discount",
    requiredFeature: "OFFER_DISCOUNTS_STAFF_FEATURE_ENABLEMENT",
  }),
  // Goodwill: moves paid_through later, and lifts a suspension.
  defineStaffAction({
    actionId: "EXTEND_SUBSCRIPTION_STAFF_ACTION",
    name: "Extend a subscription",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: "MANAGE_SUBSCRIPTIONS_STAFF_FEATURE_ENABLEMENT",
  }),
  // Enforcement: ends it now and stops the provider charging.
  defineStaffAction({
    actionId: "END_SUBSCRIPTION_STAFF_ACTION",
    name: "End a subscription",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: "MANAGE_SUBSCRIPTIONS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "RECONCILE_PAYMENTS_STAFF_ACTION",
    name: "Reconcile payments",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: "RECONCILE_PAYMENTS_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineStaffAction({
    actionId: "EXPORT_PAYMENTS_STAFF_ACTION",
    name: "Export payments",
    area: "payments",
    effect: "read",
    target: "platform_fee_payment",
    requiredFeature: "EXPORT_PAYMENTS_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
  }),

  // ── Staff: trust and safety ─────────────────────────────────────────────
  defineStaffAction({
    actionId: "OPEN_SAFETY_REPORT_STAFF_ACTION",
    name: "Open a safety report",
    area: "safety",
    effect: "write",
    target: "safety_report",
    requiredFeature: "HANDLE_REPORTS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "ADD_REPORT_EVIDENCE_STAFF_ACTION",
    name: "Add evidence to a report",
    area: "safety",
    effect: "write",
    target: "safety_report",
    requiredFeature: "HANDLE_REPORTS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "SET_REPORT_SEVERITY_STAFF_ACTION",
    name: "Set a report's severity",
    area: "safety",
    effect: "write",
    target: "safety_report",
    requiredFeature: "SET_REPORT_SEVERITY_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "SUSPEND_PENDING_INVESTIGATION_STAFF_ACTION",
    name: "Suspend an account pending investigation",
    area: "safety",
    effect: "write",
    target: "user",
    requiredFeature: "SUSPEND_PENDING_INVESTIGATION_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "LIFT_SUSPENSION_STAFF_ACTION",
    name: "Lift a suspension",
    area: "safety",
    effect: "write",
    target: "user",
    requiredFeature: "SUSPEND_PENDING_INVESTIGATION_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "ISSUE_VERDICT_STAFF_ACTION",
    name: "Issue a verdict",
    area: "safety",
    effect: "write",
    target: "safety_report",
    requiredFeature: "ISSUE_VERDICTS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "BAN_PERMANENTLY_STAFF_ACTION",
    name: "Ban an account permanently",
    area: "safety",
    effect: "write",
    target: "user",
    requiredFeature: "BAN_PERMANENTLY_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "DECIDE_APPEAL_STAFF_ACTION",
    name: "Decide an appeal",
    area: "safety",
    effect: "write",
    target: "safety_report",
    requiredFeature: "HANDLE_APPEALS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "REFER_TO_POLICE_STAFF_ACTION",
    name: "Refer a case to the police",
    area: "safety",
    effect: "write",
    target: "police_referral",
    requiredFeature: "REFER_TO_POLICE_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "SHARE_DATA_WITH_POLICE_STAFF_ACTION",
    name: "Share data with the police",
    area: "safety",
    effect: "write",
    target: "police_referral",
    requiredFeature: "SHARE_DATA_WITH_POLICE_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
  }),
  defineStaffAction({
    actionId: "PLACE_LEGAL_HOLD_STAFF_ACTION",
    name: "Place a legal hold",
    area: "safety",
    effect: "write",
    target: "legal_hold",
    requiredFeature: "PLACE_LEGAL_HOLDS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "RELEASE_LEGAL_HOLD_STAFF_ACTION",
    name: "Release a legal hold",
    area: "safety",
    effect: "write",
    target: "legal_hold",
    requiredFeature: "PLACE_LEGAL_HOLDS_STAFF_FEATURE_ENABLEMENT",
  }),
  defineStaffAction({
    actionId: "HIDE_REVIEW_STAFF_ACTION",
    name: "Hide a review",
    area: "reviews",
    effect: "write",
    target: "review",
    requiredFeature: "MODERATE_REVIEWS_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineStaffAction({
    actionId: "RESTORE_REVIEW_STAFF_ACTION",
    name: "Restore a review",
    area: "reviews",
    effect: "write",
    target: "review",
    requiredFeature: "MODERATE_REVIEWS_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),

  // ── Staff: messaging ────────────────────────────────────────────────────
  // Staff never text customers (WALLIYO-0098): retired before being built.
  defineStaffAction({
    actionId: "SEND_SMS_STAFF_ACTION",
    name: "Send an SMS",
    area: "messaging",
    effect: "write",
    target: "user",
    requiredFeature: "SEND_SMS_STAFF_FEATURE_ENABLEMENT",
    status: "retired",
  }),
  defineStaffAction({
    actionId: "SEND_BULK_SMS_STAFF_ACTION",
    name: "Send an SMS to many people",
    area: "messaging",
    effect: "write",
    target: "user",
    requiredFeature: "SEND_BULK_SMS_STAFF_FEATURE_ENABLEMENT",
    status: "retired",
  }),
  defineStaffAction({
    actionId: "EDIT_SMS_TEMPLATE_STAFF_ACTION",
    name: "Edit an SMS template",
    area: "messaging",
    effect: "write",
    target: "sms_template",
    requiredFeature: "EDIT_SMS_TEMPLATES_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),

  // ── Staff: configuration ────────────────────────────────────────────────
  defineStaffAction({
    actionId: "SET_USER_FEATURE_ENABLEMENT_STAFF_ACTION",
    name: "Switch a customer feature on or off",
    area: "settings",
    effect: "write",
    target: "setting_value",
    requiredFeature: "MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT",
    // Fonctionnalités (WALLIYO-0035): setting or removing a value.
    status: "active",
  }),
  defineStaffAction({
    actionId: "CREATE_RULE_VERSION_STAFF_ACTION",
    name: "Change a company rule",
    area: "settings",
    effect: "write",
    target: "rule_version",
    requiredFeature: "EDIT_RULES_STAFF_FEATURE_ENABLEMENT",
    // Règles (WALLIYO-0035).
    status: "active",
  }),
  defineStaffAction({
    actionId: "EDIT_PRICING_STAFF_ACTION",
    name: "Edit the fee tiers",
    area: "payments",
    effect: "write",
    target: "catalog",
    requiredFeature: "EDIT_PRICING_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineStaffAction({
    actionId: "EDIT_CATEGORIES_STAFF_ACTION",
    name: "Edit the trades",
    area: "settings",
    effect: "write",
    target: "catalog",
    requiredFeature: "EDIT_CATEGORIES_STAFF_FEATURE_ENABLEMENT",
    status: "planned",
  }),

  // ── Staff: staff and access ─────────────────────────────────────────────
  defineStaffAction({
    actionId: "INVITE_STAFF_STAFF_ACTION",
    name: "Invite a staff member",
    area: "staff",
    effect: "write",
    target: "staff_member",
    requiredFeature: "INVITE_STAFF_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "DEACTIVATE_STAFF_STAFF_ACTION",
    name: "Deactivate a staff member",
    area: "staff",
    effect: "write",
    target: "staff_member",
    requiredFeature: "DEACTIVATE_STAFF_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    // The undo for deactivating (WALLIYO-0121). Gated on the same feature:
    // whoever may take someone's access away may give it back, and splitting
    // them would leave a console where access can only ever be destroyed.
    actionId: "REACTIVATE_STAFF_STAFF_ACTION",
    name: "Reactivate a staff member",
    area: "staff",
    effect: "write",
    target: "staff_member",
    requiredFeature: "DEACTIVATE_STAFF_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "END_STAFF_SESSION_STAFF_ACTION",
    name: "End a staff member's sessions",
    area: "staff",
    effect: "write",
    target: "staff_session",
    requiredFeature: "END_STAFF_SESSIONS_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "CREATE_ROLE_STAFF_ACTION",
    name: "Create a role",
    area: "staff",
    effect: "write",
    target: "staff_role",
    requiredFeature: "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  // Teams (WALLIYO-0131). Creating one and setting who is on it are separate
  // actions because they are separate questions: "does this team exist" and
  // "whose job is this". Membership is one action for a whole table of
  // changes, saved in one transaction like every other Configuration screen.
  // Asking for a permission, and answering (WALLIYO-0131). Asking needs no
  // feature at all: anybody here may ask for what they do not hold, and being
  // refused is a normal answer. Deciding is `SET_STAFF_PERMISSION`, which
  // already exists — a grant is a grant however it was asked for.
  defineStaffAction({
    actionId: "REQUEST_PERMISSION_STAFF_ACTION",
    name: "Ask for a permission",
    area: "staff",
    effect: "write",
    target: "support_case",
    requiredFeature: null,
    status: "active",
  }),
  defineStaffAction({
    actionId: "DECIDE_PERMISSION_REQUEST_STAFF_ACTION",
    name: "Answer a request for a permission",
    area: "staff",
    effect: "write",
    target: "support_case",
    requiredFeature: null,
    status: "active",
  }),
  // ── Human Resources (WALLIYO-0131) ─────────────────────────────────────
  // Asking for leave needs no feature: everybody has leave, and a feature on
  // asking would mean somebody could be locked out of their own holiday. It is
  // decided by whoever leads their team, which is the `leads` flag on the
  // membership — so deciding needs no feature either. The rest is the HR
  // function proper, and one feature opens all of it.
  // Switching an action on for somebody writes the feature behind it, but the
  // thing that was done is not "a feature enablement was set" — it is "this
  // action was switched on for this person". An audit that records the
  // mechanism is one somebody has to reverse-engineer the intent from
  // (WALLIYO-0131).
  defineStaffAction({
    actionId: "UPDATE_ACTION_ENABLEMENT_STAFF_ACTION",
    name: "Update an action enablement",
    area: "staff",
    effect: "write",
    target: "setting_value",
    requiredFeature: "MANAGE_ACTION_ENABLEMENTS_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "REQUEST_TIME_OFF_STAFF_ACTION",
    name: "Ask for time off",
    area: "staff",
    effect: "write",
    target: "time_off_request",
    requiredFeature: null,
    status: "active",
  }),
  defineStaffAction({
    actionId: "DECIDE_TIME_OFF_STAFF_ACTION",
    name: "Decide a time off request",
    area: "staff",
    effect: "write",
    target: "time_off_request",
    requiredFeature: null,
    status: "active",
  }),
  defineStaffAction({
    actionId: "SET_EMPLOYMENT_STAFF_ACTION",
    name: "Edit an employment record",
    area: "staff",
    effect: "write",
    target: "staff_employment",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "OPEN_JOB_STAFF_ACTION",
    name: "Open a vacancy",
    area: "staff",
    effect: "write",
    target: "job_opening",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "EDIT_JOB_STAFF_ACTION",
    name: "Edit a vacancy",
    area: "staff",
    effect: "write",
    target: "job_opening",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "MOVE_CANDIDATE_STAFF_ACTION",
    name: "Move a candidate",
    area: "staff",
    effect: "write",
    target: "job_candidate",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "OPEN_PAYROLL_STAFF_ACTION",
    name: "Open a payroll month",
    area: "staff",
    effect: "write",
    target: "payroll_run",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "EDIT_PAYROLL_LINE_STAFF_ACTION",
    name: "Hold or release a payroll line",
    area: "staff",
    effect: "write",
    target: "payroll_run",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "CLOSE_PAYROLL_STAFF_ACTION",
    name: "Close a payroll month",
    area: "staff",
    effect: "write",
    target: "payroll_run",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "OPEN_REVIEW_CYCLE_STAFF_ACTION",
    name: "Open a review cycle",
    area: "staff",
    effect: "write",
    target: "performance_review",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "WRITE_REVIEW_STAFF_ACTION",
    name: "Write a review",
    area: "staff",
    effect: "write",
    target: "performance_review",
    requiredFeature: null,
    status: "active",
  }),
  defineStaffAction({
    actionId: "EDIT_COURSE_STAFF_ACTION",
    name: "Add or change a course",
    area: "staff",
    effect: "write",
    target: "course",
    requiredFeature: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "ENROL_IN_COURSE_STAFF_ACTION",
    name: "Enrol in a course",
    area: "staff",
    effect: "write",
    target: "course",
    requiredFeature: null,
    status: "active",
  }),
  defineStaffAction({
    actionId: "CREATE_TEAM_STAFF_ACTION",
    name: "Create a team",
    area: "staff",
    effect: "write",
    target: "staff_team",
    requiredFeature: "MANAGE_TEAMS_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "EDIT_TEAM_STAFF_ACTION",
    name: "Edit a team",
    area: "staff",
    effect: "write",
    target: "staff_team",
    requiredFeature: "MANAGE_TEAMS_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "SET_TEAM_MEMBERSHIP_STAFF_ACTION",
    name: "Set who is on a team",
    area: "staff",
    effect: "write",
    target: "staff_team",
    requiredFeature: "MANAGE_TEAMS_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "EDIT_ROLE_STAFF_ACTION",
    name: "Edit a role",
    area: "staff",
    effect: "write",
    target: "staff_role",
    requiredFeature: "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  // Granting or revoking a permission on top of a feature, and its limit
  // (ADR-0019). Who may grant which is decided by sensitivity on the server,
  // read for the permission.
  defineStaffAction({
    actionId: "SET_STAFF_PERMISSION_STAFF_ACTION",
    name: "Grant or revoke a permission",
    area: "staff",
    effect: "write",
    target: "setting_value",
    requiredFeature: "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT",
    alsoAllowedBy: ["ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT"],
    status: "active",
  }),
  defineStaffAction({
    actionId: "SET_STAFF_FEATURE_ENABLEMENT_STAFF_ACTION",
    name: "Grant or revoke a feature",
    area: "staff",
    effect: "write",
    target: "setting_value",
    requiredFeature: "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT",
    // Low-sensitivity features may also be granted by whoever assigns roles.
    // Which features each may grant is decided by sensitivity on the server.
    alsoAllowedBy: ["ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT"],
    status: "active",
  }),
  defineStaffAction({
    actionId: "ASSIGN_ROLE_STAFF_ACTION",
    name: "Assign a role",
    area: "staff",
    effect: "write",
    target: "staff_member",
    requiredFeature: "ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),
  defineStaffAction({
    actionId: "REMOVE_ROLE_STAFF_ACTION",
    name: "Remove a role",
    area: "staff",
    effect: "write",
    target: "staff_member",
    requiredFeature: "ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),

  // ── Staff: audit ────────────────────────────────────────────────────────
  defineStaffAction({
    actionId: "VIEW_AUDIT_LOG_STAFF_ACTION",
    name: "View the audit log",
    area: "audit",
    effect: "read",
    target: "audit_log",
    requiredFeature: "VIEW_AUDIT_LOG_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
    status: "active",
  }),
  defineStaffAction({
    actionId: "VIEW_SENSITIVE_AUDIT_VALUES_STAFF_ACTION",
    name: "View sensitive audit values",
    area: "audit",
    effect: "read",
    target: "audit_log",
    requiredFeature: "VIEW_SENSITIVE_AUDIT_VALUES_STAFF_FEATURE_ENABLEMENT",
    sensitive: true,
    status: "active",
  }),
  defineStaffAction({
    actionId: "VIEW_HEALTH_DASHBOARD_STAFF_ACTION",
    name: "View the health dashboard",
    area: "audit",
    effect: "read",
    target: "catalog",
    requiredFeature: "VIEW_HEALTH_DASHBOARD_STAFF_FEATURE_ENABLEMENT",
    status: "active",
  }),

  // ── System: schedules, webhooks, consequences ───────────────────────────
  defineSystemAction({
    actionId: "EXPIRE_JOB_SYSTEM_ACTION",
    name: "Expire a job nobody answered",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: null,
    status: "planned",
  }),
  defineSystemAction({
    actionId: "EXPIRE_QUOTE_SYSTEM_ACTION",
    name: "Expire a quote",
    area: "jobs",
    effect: "write",
    target: "quote",
    requiredFeature: null,
    status: "planned",
  }),
  defineSystemAction({
    actionId: "CLOSE_OTHER_QUOTES_SYSTEM_ACTION",
    name: "Close the other quotes",
    area: "jobs",
    effect: "write",
    target: "quote",
    requiredFeature: null,
    status: "planned",
  }),
  defineSystemAction({
    actionId: "OPEN_CONVERSATION_ON_QUOTE_SYSTEM_ACTION",
    name: "Open a conversation on a quote",
    area: "messaging",
    effect: "write",
    target: "conversation",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "NOTIFY_USER_SYSTEM_ACTION",
    name: "Send a notification",
    area: "messaging",
    effect: "write",
    target: "user",
    requiredFeature: null,
  }),
  // A new lead, told to the pro in the app (WALLIYO-0098: never SMS).
  defineSystemAction({
    actionId: "NOTIFY_NEW_LEAD_SYSTEM_ACTION",
    name: "Notify a pro of a new lead",
    area: "leads",
    effect: "write",
    target: "user",
    requiredFeature: "LEAD_NOTIFICATIONS_USER_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineSystemAction({
    actionId: "SEND_LEAD_SMS_SYSTEM_ACTION",
    name: "Send a lead by SMS",
    area: "leads",
    effect: "write",
    target: "user",
    requiredFeature: "SMS_LEADS_USER_FEATURE_ENABLEMENT",
    status: "retired",
  }),
  // A reminder before a scheduled job: an in-app notification, never SMS.
  defineSystemAction({
    actionId: "NOTIFY_JOB_REMINDER_SYSTEM_ACTION",
    name: "Send a job reminder",
    area: "jobs",
    effect: "write",
    target: "job",
    requiredFeature: "JOB_REMINDERS_USER_FEATURE_ENABLEMENT",
    status: "planned",
  }),
  defineSystemAction({
    actionId: "CONFIRM_FEE_PAYMENT_SYSTEM_ACTION",
    name: "Confirm a fee payment",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "FAIL_FEE_PAYMENT_SYSTEM_ACTION",
    name: "Record a failed fee payment",
    area: "payments",
    effect: "write",
    target: "platform_fee_payment",
    requiredFeature: null,
  }),
  // What billing does when a provider's event, or the clock,
  // moves a subscription (ADR-0018 §3): a first payment activates it, a later
  // one renews it, an unpaid renewal suspends it, and it expires when the
  // grace period or a cancelled period runs out.
  defineSystemAction({
    actionId: "ACTIVATE_SUBSCRIPTION_SYSTEM_ACTION",
    name: "Activate a subscription",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "SUSPEND_SUBSCRIPTION_SYSTEM_ACTION",
    name: "Suspend a subscription",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "RENEW_SUBSCRIPTION_SYSTEM_ACTION",
    name: "Renew a subscription",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "EXPIRE_SUBSCRIPTION_SYSTEM_ACTION",
    name: "End a lapsed subscription",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "REMIND_SUBSCRIPTION_EXPIRY_SYSTEM_ACTION",
    name: "Remind a pro their plan is ending",
    area: "payments",
    effect: "write",
    target: "pro_subscription",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "EXPIRE_SETTING_VALUE_SYSTEM_ACTION",
    name: "Expire a setting value",
    area: "settings",
    effect: "write",
    target: "setting_value",
    requiredFeature: null,
    status: "planned",
  }),
  defineSystemAction({
    actionId: "DELETE_ID_IMAGES_SYSTEM_ACTION",
    name: "Delete ID images",
    area: "onboarding",
    effect: "write",
    target: "identity_verification",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "FLAG_BANNED_DOCUMENT_SYSTEM_ACTION",
    name: "Flag a banned document",
    area: "safety",
    effect: "write",
    target: "identity_verification",
    requiredFeature: null,
  }),
  defineSystemAction({
    actionId: "SUSPEND_ON_CRITICAL_REPORT_SYSTEM_ACTION",
    name: "Suspend on a critical report",
    area: "safety",
    effect: "write",
    target: "user",
    requiredFeature: null,
  }),
];

export type ActionId = (typeof ACTIONS)[number]["actionId"];
